import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PersonDetailed } from 'app/core/models/person/person-detailed';
import { PersonSearchParameters } from 'app/core/models/person/person-detailed-search';
import { SECURITY_ROLE_KEY } from 'app/core/models/security/security-role-keys';
import { TRAINING_PROVIDER_PERSON_TYPE } from 'app/core/models/training-provider/training-provider';
import { AuthService } from 'app/core/services/auth/auth.service';
import { PersonService } from 'app/core/services/person/person.service';
import { SelectedPersonService } from 'app/core/services/selected-person/selected-person.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

@Component({
  selector: 'sqep-person-selector',
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './person-selector.component.html',
  styleUrl: './person-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonSelectorComponent {
  private readonly personService = inject(PersonService);
  private readonly authService = inject(AuthService);
  private readonly selectedPersonService = inject(SelectedPersonService);
  private readonly loggedInUser = toSignal(this.authService.loggedInUserSubj);
  protected readonly selectedPerson = this.selectedPersonService.selectedPerson;

  // Only a provider admin link confines a person to their own training
  // providers. A Trainer link (often added by the Cornerstone sync) confines
  // nobody, so it must not take the box away from anyone who qualifies by
  // role or as a manager (#9825).
  private readonly isTrainingProviderAdmin = computed(
    () =>
      this.loggedInUser()?.trainingProviderAssignments?.some(
        (a) =>
          a.trainingProviderPersonTypeId ===
          TRAINING_PROVIDER_PERSON_TYPE.Admin,
      ) ?? false,
  );

  protected readonly canViewOtherUsers = computed(() => {
    const person = this.loggedInUser();
    if (!person) return false;
    // A Superuser is never confined by a provider admin link, the same
    // exemption notTrainingProviderAdminGuard and the side menu make.
    if (person.roleIdentifier === SECURITY_ROLE_KEY.Superuser) return true;
    if (this.isTrainingProviderAdmin()) return false;
    return (
      this.authService.isFullAdmin ||
      person.roleIdentifier === SECURITY_ROLE_KEY.OuResponsible ||
      person.managerTypeId > 0
    );
  });

  protected readonly searchControl = new FormControl<
    PersonDetailed | string | null
  >(null);

  // The search API is scoped server-side by isViewUserData - admins see
  // everyone, managers see their subordinates, OU responsibles see their OU,
  // etc. - so the client doesn't need to duplicate that restriction, only
  // gate whether the dropdown is shown at all (canViewOtherUsers above).
  protected readonly peopleOptions = toSignal(
    merge(
      toObservable(this.loggedInUser).pipe(
        filter((user) => !!user),
        take(1),
        map(() => ''),
      ),
      this.searchControl.valueChanges.pipe(
        filter((v): v is string => typeof v === 'string'),
        tap((term) => {
          if (term.trim() === '' && this.selectedPerson() !== null) {
            this.selectedPersonService.clearSelection();
          }
        }),
      ),
    ).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const params: PersonSearchParameters = {
          search: term,
          isViewUserData: true,
          page: 1,
          pageSize: 50,
        };
        return this.personService.getPeopleDetailed(params).pipe(
          map((result) =>
            result.data.filter((p) => p.id !== this.loggedInUser()?.id),
          ),
          catchError(() => of([] as PersonDetailed[])),
        );
      }),
    ),
    { initialValue: [] as PersonDetailed[] },
  );

  // Mirrors the selection both ways, so a person chosen elsewhere (e.g. the
  // claims Dashboard's "View Home as") shows here as if picked in this box.
  private readonly _syncSelection = effect(() => {
    const selected = this.selectedPerson();
    const current = this.searchControl.value;
    if (selected === null) {
      this.searchControl.setValue(null, { emitEvent: false });
    } else if (typeof current !== 'object' || current?.id !== selected.id) {
      this.searchControl.setValue(selected, { emitEvent: false });
    }
  });

  protected displayFn(person: PersonDetailed | string | null): string {
    if (!person || typeof person === 'string') return '';
    return person.displayName;
  }

  protected onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedPersonService.setSelection(
      event.option.value as PersonDetailed,
    );
  }

  protected clearSelection(event: MouseEvent): void {
    event.stopPropagation();
    this.searchControl.setValue(null, { emitEvent: false });
    this.selectedPersonService.clearSelection();
  }
}
